import { memo, useEffect, useState, useCallback } from 'react';
import { X, PanelLeft, Keyboard } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { logger } from '@/lib/logger';

const STORAGE_KEY = 'iskool:course-onboarding-seen-v2';

interface OnboardingTip {
  icon: typeof PanelLeft;
  title: string;
  body: string;
  keybind?: string;
}

const TIPS: readonly OnboardingTip[] = [
  {
    icon: PanelLeft,
    title: 'Course outline',
    body: 'Hover the left edge of the screen to peek at the chapter outline. Click the toggle in the header to pin it open.',
  },
  {
    icon: Keyboard,
    title: 'Keyboard shortcuts',
    body: 'Press ? anywhere to open the searchable shortcut cheatsheet.',
    keybind: '?',
  },
];

function getMacKey(keybind: string): string {
  if (typeof navigator === 'undefined') return keybind;
  if (!navigator.platform.includes('Mac')) return keybind;
  return keybind.replace('Cmd/Ctrl', '⌘').replace('Cmd', '⌘').replace('Ctrl', 'Ctrl');
}

/**
 * First-run onboarding for the course workspace. Surfaces easy-to-miss
 * workspace affordances (outline peek, cheatsheet) once per device, then
 * never again. localStorage flag is versioned so we can re-introduce it
 * when affordances change — bumped to v2 in 2026-04-22 when H14 retired
 * Focus mode (tip removed).
 */
export const CourseOnboardingCard = memo(function CourseOnboardingCard() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      const seen = window.localStorage.getItem(STORAGE_KEY);
      if (!seen) setVisible(true);
    } catch (err) {
      // Private-mode storage block, etc. — skip onboarding silently.
      logger.debug('onboarding-storage-read-failed', { err });
    }
  }, []);

  const dismiss = useCallback(() => {
    setVisible(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch (err) {
      logger.debug('onboarding-storage-write-failed', { err });
    }
  }, []);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="onboarding-title"
      className="fixed bottom-4 left-4 z-50 w-[360px] max-w-[calc(100vw-2rem)] rounded-xl border border-border bg-card shadow-xl p-4 space-y-3"
    >
      <div className="flex items-start justify-between">
        <h2 id="onboarding-title" className="text-sm font-semibold">
          Two things worth knowing
        </h2>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss onboarding"
          className="text-muted-foreground hover:text-foreground -mr-1 -mt-1 p-1"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <ul className="space-y-3">
        {TIPS.map((tip) => {
          const Icon = tip.icon;
          return (
            <li key={tip.title} className="flex gap-3">
              <div className="shrink-0 mt-0.5">
                <Icon className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0 space-y-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-medium">{tip.title}</span>
                  {tip.keybind && (
                    <kbd className="text-badge px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-mono">
                      {getMacKey(tip.keybind)}
                    </kbd>
                  )}
                </div>
                <p className="text-xs text-muted-foreground leading-snug">{tip.body}</p>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="flex justify-end pt-1">
        <Button size="sm" onClick={dismiss}>
          Got it
        </Button>
      </div>
    </div>
  );
});
